"use client";

import React, { FC, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, Info } from "lucide-react";
import {
  FundabilityNarrative,
  FundingOption,
  LandingEvaluationResponse,
  LoanType,
} from "@cxm/client";
import { currency, loanTypes, shortCurrency } from "@cxm/lib";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./dialog";
import { Button } from "./button";
import { SBA_MORE_INFORMATION_URL } from "./onboard-widget-cta";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table";
import Wheel from "./wheel";
import FundabilityBreakdown from "./fundability-breakdown";
import { IndicativeLoanOptionsCard } from "./evaluation-chrome";

// Fundability-score styling helper for this shared evaluation-results
// component, intended to back both the borrower and referral wizards.
export const getFundabilityScoreStyles = (score: number | undefined) => {
  if (score === undefined) {
    return { textColor: "text-gray-400", borderColor: "border-gray-200", label: "---" };
  }
  if (score <= 2) {
    return { textColor: "text-red-500", borderColor: "border-red-200", label: "Poor" };
  } else if (score >= 6 && score <= 8) {
    return { textColor: "text-green-500", borderColor: "border-green-200", label: "Good" };
  } else if (score >= 3 && score <= 5) {
    return { textColor: "text-yellow-500", borderColor: "border-yellow-200", label: "Fair" };
  } else {
    return { textColor: "text-blue-500", borderColor: "border-blue-200", label: "Excellent" };
  }
};

// Per-loan-type asset inputs used to render the funding-option calculation
// breakdown (the "unencumbered assets" detail) inside the Wheel popover. The
// breakdown is computed entirely client-side from these values and the
// advance-rate constants below — it never reads the evaluation API response.
export type FundingBreakdownInputs = {
  totalEquipment?: number;
  encumberedEquipment?: number;
  totalRealEstate?: number;
  encumberedRealEstate?: number;
  totalInventory?: number;
  encumberedInventory?: number;
  monthlyAr?: number;
  po?: number;
};

// The per-loan-type calculation breakdown shown beneath a loan type in the
// Wheel popover. Only ABL / AR_FACTORING / PURCHASE_ORDER have a breakdown;
// every other loan type renders nothing here (description only). Extracted
// from the borrower wizard's former private `loanCalcs` so the borrower
// simulation and the lender referral wizard can share one copy of the math.
export const FundingOptionBreakdown: FC<{
  loanType?: LoanType;
  inputs: FundingBreakdownInputs;
}> = ({ loanType, inputs }) => {
  if (loanType === LoanType.ABL) {
    const unencumberedEquipment =
      ((inputs.totalEquipment ?? 0) - (inputs.encumberedEquipment ?? 0)) * 0.9;
    const unencumberedRealEstate =
      ((inputs.totalRealEstate ?? 0) - (inputs.encumberedRealEstate ?? 0)) *
      0.75;
    const unencumberedInventory =
      ((inputs.totalInventory ?? 0) - (inputs.encumberedInventory ?? 0)) * 0.8;
    return (
      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="font-semibold text-blue-900 mb-2">
          Asset-Based Loan Amount Calculation
        </h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span>Unencumbered Equipment (90%):</span>
            <span className="font-medium">
              {currency(unencumberedEquipment)}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Unencumbered Real Estate (75%):</span>
            <span className="font-medium">
              {currency(unencumberedRealEstate)}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Unencumbered Inventory (80%):</span>
            <span className="font-medium">
              {currency(unencumberedInventory)}
            </span>
          </div>
          <hr className="border-blue-300" />
          <div className="flex justify-between font-semibold text-blue-900">
            <span>Estimated Maximum Loan Amount:</span>
            <span>
              {currency(
                unencumberedEquipment +
                  unencumberedRealEstate +
                  unencumberedInventory,
              )}
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (loanType === LoanType.AR_FACTORING) {
    const monthlyAr = inputs.monthlyAr ?? 0;
    return (
      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="font-semibold text-blue-900 mb-2">
          Accounts Receivable Factoring
        </h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span>Average Monthly Accounts Receivable:</span>
            <span className="font-medium">{currency(monthlyAr)}</span>
          </div>
          <div className="flex justify-end">
            <span>x3 (90 Days)</span>
          </div>
          <div className="flex justify-between">
            <span>Estimated Total Accounts Receivable:</span>
            <span className="font-medium">{currency(monthlyAr * 3)}</span>
          </div>
          <hr className="border-blue-300" />
          <div className="flex justify-between font-semibold text-blue-900">
            <span>Estimated Potential Advance (85%):</span>
            <span>{currency(monthlyAr * 3 * 0.85)}</span>
          </div>
        </div>
      </div>
    );
  }

  if (loanType === LoanType.PURCHASE_ORDER) {
    const po = inputs.po ?? 0;
    return (
      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="font-semibold text-blue-900 mb-2">
          Purchase Order Financing
        </h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span>Average Monthly Purchase Orders:</span>
            <span className="font-medium">{currency(po)}</span>
          </div>
          <div className="flex justify-end">
            <span>x3 (90 Days)</span>
          </div>
          <div className="flex justify-between">
            <span>Estimated Total Purchase Orders:</span>
            <span className="font-medium">{currency(po * 3)}</span>
          </div>
          <hr className="border-blue-300" />
          <div className="flex justify-between font-semibold text-blue-900">
            <span>Estimated Potential Advance (85%):</span>
            <span>{currency(po * 3 * 0.85)}</span>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// Builds the per-loan-type description nodes consumed by the Wheel. When
// `inputs` is provided, each description is followed by the funding-option
// calculation breakdown (the unencumbered-assets detail) for that loan type.
export const buildLoanTypeDescriptions = (
  inputs?: FundingBreakdownInputs,
): Record<string, React.ReactNode> =>
  loanTypes.reduce((acc, loanType) => {
    acc[loanType.key] = (
      <div>
        <p className="text-sm text-gray-600 whitespace-pre-line">
          {loanType.longDescription}
        </p>
        {inputs && (
          <FundingOptionBreakdown loanType={loanType.key} inputs={inputs} />
        )}
      </div>
    );
    return acc;
  }, {} as Record<string, React.ReactNode>);

type FundabilityScoreCardProps = {
  score?: number;
  narrative?: FundabilityNarrative[];
};

export const FundabilityScoreCard: FC<FundabilityScoreCardProps> = ({
  score,
  narrative = [],
}) => {
  const scoreStyles = getFundabilityScoreStyles(score);

  return (
    <div
      className={`rounded-lg border-2 ${scoreStyles.borderColor} bg-white shadow-sm`}
    >
      <div className="relative flex flex-col items-center justify-center gap-4 p-4 pt-8">
        <div className="text-center">
          <h2 className="text-lg font-semibold">
            Company Fundability Score and Funding Choices
          </h2>
          <p className="text-xs text-muted-foreground">
            These results illustrate potential options based on your inputs.
          </p>
          {narrative.length > 0 && (
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="absolute right-4 top-1 h-[30px] items-center"
                >
                  <Info className="h-4 w-4" />
                  <span className="ml-1 text-xs font-medium">
                    Understand Your Score
                  </span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Key Insights</DialogTitle>
                </DialogHeader>
                <div className="mt-4">
                  <FundabilityBreakdown narrative={narrative} />
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
        {score !== undefined ? (
          <div className="flex w-full flex-col items-center">
            <div className="flex flex-col items-center space-y-2">
              <span className={`text-4xl font-bold ${scoreStyles.textColor}`}>
                {scoreStyles.label}
              </span>
              <span
                className={`text-2xl font-semibold ${scoreStyles.textColor}`}
              >
                {score}/10
              </span>
            </div>
            <div className="mt-4 h-4 w-full max-w-md overflow-hidden rounded-full bg-gray-200">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${Math.min(Math.max((score / 10) * 100, 0), 100)}%`,
                  background: "linear-gradient(to right, #ef4444, #eab308, #22c55e)",
                }}
              />
            </div>
          </div>
        ) : (
          <div className="flex min-h-[160px] flex-col items-center justify-center gap-2 text-sm text-gray-500">
            <span className="text-3xl font-semibold text-gray-300">---</span>
            <span>
              Your fundability score will appear after you calculate results.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export type FundingOptionsSortField =
  | "loanType"
  | "potential"
  | "payment"
  | "rate"
  | "term"
  | "lenders"
  | "timeline";

type FundingOptionsSortDirection = "asc" | "desc" | null;

type FundingOptionsTableProps = {
  options: FundingOption[];
  matchedLenders?: Partial<Record<LoanType, number>>;
  selectedOption?: FundingOption | null;
  onOptionSelect?: (option: FundingOption) => void;
  sortable?: boolean;
  loanAmount?: number;
  // Rich per-loan-type description nodes (description + funding-option
  // calculation breakdown) for the row-click dialog. When a loan type is
  // missing from the map the dialog falls back to the plain description.
  loanTypeDescriptions?: Record<string, React.ReactNode>;
};

export const FundingOptionsTable: FC<FundingOptionsTableProps> = ({
  options,
  matchedLenders = {},
  selectedOption,
  onOptionSelect,
  sortable = false,
  loanAmount,
  loanTypeDescriptions,
}) => {
  const [sortField, setSortField] = useState<FundingOptionsSortField | null>(
    null,
  );
  const [sortDirection, setSortDirection] = useState<FundingOptionsSortDirection>(
    null,
  );

  const handleSort = (field: FundingOptionsSortField) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> null
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortField(null);
        setSortDirection(null);
      } else {
        setSortDirection("asc");
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field: FundingOptionsSortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-1 h-4 w-4 text-gray-400" />;
    }
    if (sortDirection === "asc") {
      return <ArrowUp className="ml-1 h-4 w-4" />;
    }
    if (sortDirection === "desc") {
      return <ArrowDown className="ml-1 h-4 w-4" />;
    }
    return <ArrowUpDown className="ml-1 h-4 w-4 text-gray-400" />;
  };

  const sortFundingOptions = (toSort: FundingOption[]) => {
    if (!sortField || !sortDirection) {
      // Default sorting logic when no manual sort is applied
      return [...toSort].sort((a, b) => {
        // Sort by matched lenders and funding time
        let aVal = (matchedLenders?.[a.loanType ?? LoanType.ABL] ?? 0) > 0
          ? (a.lowPayment ?? 1000000)
          : 900000 + (a.lowPayment ?? 1000000);

        // push po/ar to the bottom
        if (
          a.loanType == LoanType.AR_FACTORING ||
          a.loanType == LoanType.PURCHASE_ORDER
        ) {
          aVal += 80000000;
        }

        let bVal = (matchedLenders?.[b.loanType ?? LoanType.ABL] ?? 0) > 0
          ? (b.lowPayment ?? 1000000)
          : 900000 + (b.lowPayment ?? 1000000);

        // push po/ar to the bottom
        if (
          b.loanType == LoanType.AR_FACTORING ||
          b.loanType == LoanType.PURCHASE_ORDER
        ) {
          bVal += 80000000;
        }

        return aVal - bVal;
      });
    }

    const sortedOptions = [...toSort].sort((a, b) => {
      let aVal: string | number, bVal: string | number;

      switch (sortField) {
        case "loanType": {
          const aLoanType = loanTypes.find((x) => x.key === a.loanType);
          const bLoanType = loanTypes.find((x) => x.key === b.loanType);
          aVal = aLoanType?.name || a.loanType || "";
          bVal = bLoanType?.name || b.loanType || "";
          break;
        }
        case "rate":
          aVal = a.estimatedRate ?? 0;
          bVal = b.estimatedRate ?? 0;
          break;
        case "term":
          aVal = a.highMaturity ?? 0;
          bVal = b.highMaturity ?? 0;
          break;
        case "potential":
          aVal = a.highAmount ?? 0;
          bVal = b.highAmount ?? 0;
          break;
        case "payment":
          aVal = a.lowPayment ?? 100000;
          bVal = b.lowPayment ?? 100000;
          break;
        case "lenders":
          aVal = matchedLenders?.[a.loanType ?? LoanType.ABL] ?? 0;
          bVal = matchedLenders?.[b.loanType ?? LoanType.ABL] ?? 0;
          break;
        case "timeline":
          aVal = a.lowTime ?? 0;
          bVal = b.lowTime ?? 0;
          break;
        default:
          return 0;
      }

      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortDirection === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortDirection === "asc"
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number);
    });

    return sortedOptions;
  };

  const rows = sortable ? sortFundingOptions(options) : options;

  const renderHeadCell = (
    field: FundingOptionsSortField,
    label: string,
    className: string,
    subLine?: React.ReactNode,
  ) => (
    <TableHead className={className}>
      {sortable ? (
        <Button
          variant="ghost"
          onClick={() => handleSort(field)}
          className="h-auto px-0 text-xs font-medium uppercase tracking-wide text-muted-foreground hover:bg-transparent"
        >
          {subLine ? (
            <div className="flex flex-col">
              {label} {subLine}
            </div>
          ) : (
            label
          )}
          {getSortIcon(field)}
        </Button>
      ) : subLine ? (
        <div className="flex flex-col">
          {label} {subLine}
        </div>
      ) : (
        label
      )}
    </TableHead>
  );

  return (
    <div className="max-h-[455px] overflow-y-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow className="text-xs uppercase tracking-wide text-muted-foreground">
            {renderHeadCell("loanType", "Loan Type", "w-[200px] px-3")}
            {renderHeadCell("potential", "Max Potential", "px-3")}
            {renderHeadCell(
              "payment",
              "Monthly Payment",
              "px-3",
              loanAmount !== undefined ? (
                <span className="text-xs">
                  (out of {currency(loanAmount)})
                </span>
              ) : undefined,
            )}
            {renderHeadCell("rate", "Rate", "px-3")}
            {renderHeadCell("term", "Term", "px-3")}
            {renderHeadCell("lenders", "Matched Lenders", "px-3")}
            {renderHeadCell("timeline", "Timeline", "px-3")}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((option) => {
            const loanType = loanTypes.find((x) => x.key === option.loanType);
            const matchedLenderCount =
              matchedLenders?.[option.loanType ?? LoanType.ABL] || 0;
            const isSelected = selectedOption?.loanType === option.loanType;

            return (
              <Dialog key={`${option.loanType}-${option.highAmount ?? 0}`}>
                <DialogTrigger asChild>
                  <TableRow
                    className={`h-[52px] cursor-pointer text-sm transition-colors hover:bg-muted/40 ${isSelected ? "bg-primary/10" : ""}`}
                    onClick={() => onOptionSelect?.(option)}
                  >
                    <TableCell className="px-3 font-medium text-gray-900">
                      {loanType?.name || option.loanType}
                    </TableCell>
                    <TableCell className="px-3 text-center">
                      {(option.lowAmount && option.highAmount)
                        ? shortCurrency(option.highAmount)
                        : <span className="text-gray-400">—</span>}
                    </TableCell>
                    <TableCell className="px-3 text-center">
                      {option.lowPayment
                        ? currency(option.lowPayment)
                        : <span className="text-gray-400">—</span>}
                    </TableCell>
                    <TableCell className="px-3 text-center">
                      {(option.loanType === LoanType.AR_FACTORING ||
                        option.loanType === LoanType.PURCHASE_ORDER)
                        ? (
                          <>
                            <div className="font-semibold text-gray-900">--</div>
                            <div className="text-[10px] text-gray-500">Fee-Based</div>
                          </>
                        )
                        : (
                          <>
                            <div className="font-semibold text-gray-900">
                              {Math.round((option.estimatedRate ?? 0) * 100)}%
                            </div>
                            <div className="text-[10px] text-gray-500">Interest</div>
                          </>
                        )}
                    </TableCell>
                    <TableCell className="px-3 text-center">
                      <div className="font-semibold text-gray-900">
                        {option.highMaturity}
                      </div>
                      <div className="text-[10px] text-gray-500">Months</div>
                    </TableCell>
                    <TableCell className="px-3 text-center">
                      <div className={`font-semibold ${matchedLenderCount > 0 ? "text-green-600" : "text-gray-400"}`}>
                        {matchedLenderCount}
                      </div>
                    </TableCell>
                    <TableCell className="px-3 text-center">
                      <div className="font-semibold text-gray-900">
                        {option.lowTime} - {option.highTime}
                      </div>
                      <div className="text-[10px] text-gray-500">Days</div>
                    </TableCell>
                  </TableRow>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>
                      {loanType?.fullName || loanType?.name || option.loanType}
                    </DialogTitle>
                  </DialogHeader>
                  {/* asChild keeps the body a <div> (descriptions can contain
                      block elements) while still wiring aria-describedby. */}
                  <DialogDescription asChild>
                    <div className="mt-4 text-sm text-gray-600">
                      {(option.loanType &&
                        loanTypeDescriptions?.[option.loanType]) ?? (
                        <p className="whitespace-pre-line">
                          {loanType?.longDescription}
                        </p>
                      )}
                    </div>
                  </DialogDescription>
                  {/* Per the accepted SBA product requirement, the SBA result
                      dialog routes to the HubSpot information form; every
                      other loan type's dialog stays description-only. */}
                  {option.loanType === LoanType.SBA && (
                    <DialogFooter>
                      <Button asChild variant="success">
                        <a
                          href={SBA_MORE_INFORMATION_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          More Information
                        </a>
                      </Button>
                    </DialogFooter>
                  )}
                </DialogContent>
              </Dialog>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

type EvaluationResultsProps = {
  evaluationResults?: LandingEvaluationResponse | null;
  selectedOption?: FundingOption | null;
  onOptionSelect?: (option: FundingOption) => void;
  onContinue?: () => void;
  continueDisabled?: boolean;
  sortable?: boolean;
  loanAmount?: number;
  // Per-loan-type asset inputs that drive the funding-option calculation
  // breakdown in the Wheel popover. When omitted (description-only callers),
  // the popover shows each loan type's description without a breakdown.
  breakdownInputs?: FundingBreakdownInputs;
  // Label override for the continue CTA. Defaults to the legacy
  // "Continue to Borrower Information" so existing callers keep working;
  // the lender referral wizard overrides it to "Apply Now" to read as the
  // primary apply action rather than an internal wizard step.
  continueLabel?: string;
};

const EvaluationResults: FC<EvaluationResultsProps> = ({
  evaluationResults,
  selectedOption,
  onOptionSelect,
  onContinue,
  continueDisabled,
  sortable = false,
  loanAmount,
  breakdownInputs,
  continueLabel = "Continue to Borrower Information",
}) => {
  const fundabilityScore = evaluationResults?.fundabilityScore;
  const options = evaluationResults?.fundingOptions ?? [];
  const matchedLenders = evaluationResults?.matchedLenders ?? {};
  const narrative = evaluationResults?.narrative ?? [];
  const selectedLoanType = selectedOption?.loanType ?? options[0]?.loanType;
  const loanTypeDescriptions = useMemo(
    () => buildLoanTypeDescriptions(breakdownInputs),
    [breakdownInputs],
  );

  const slices = useMemo(
    () =>
      loanTypes
        .filter((loanType) => !loanType.excludeFromWheel)
        .map((loanType) => ({
          name: loanType.name,
          color: loanType.color ?? "#888888",
          id: loanType.key,
          disabled: (evaluationResults && !options.some((option) => option.loanType === loanType.key)) || undefined,
        })),
    [evaluationResults, options],
  );

  const handleWheelSelect = (typeName: string) => {
    const loanType = loanTypes.find((x) => x.name === typeName);
    if (!loanType) return;

    const matchingOption = options.find((option) => option.loanType === loanType.key);
    if (matchingOption) {
      onOptionSelect?.(matchingOption);
    }
  };

  return (
    <div className="space-y-6">
      <Wheel
        size={400}
        slices={slices}
        selectedType={selectedLoanType}
        onSelectType={handleWheelSelect}
        onHoverType={() => undefined}
        loanTypeDescriptions={loanTypeDescriptions}
      />

      <FundabilityScoreCard score={fundabilityScore} narrative={narrative} />

      <div className="space-y-4">
        {options.length > 0 ? (
          <div className="space-y-4 rounded-lg bg-white p-4 shadow-sm">
            <div className="flex flex-col">
              <h3 className="text-lg font-semibold">
                {options.length} Funding Options
              </h3>
              <p className="text-xs text-muted-foreground">
                These illustrative terms come from matched lenders based on your inputs.
                Review them to choose the best fit.
              </p>
            </div>
            <FundingOptionsTable
              options={options}
              matchedLenders={matchedLenders}
              selectedOption={selectedOption}
              onOptionSelect={onOptionSelect}
              sortable={sortable}
              loanAmount={loanAmount}
              loanTypeDescriptions={loanTypeDescriptions}
            />

            {onContinue && (
              <Button
                variant="success"
                onClick={onContinue}
                disabled={continueDisabled}
                className="w-full"
              >
                {continueLabel}
              </Button>
            )}
          </div>
        ) : (
          <IndicativeLoanOptionsCard />
        )}
      </div>
    </div>
  );
};

export default EvaluationResults;
